// Screen Wake Lock API utility
// Keeps the screen awake on mobile and desktop browsers that support it
// Uses native Wake Lock API with fallback to video trick for older/unsupported browsers

let wakeLock: WakeLockSentinel | null = null;
let noSleepVideo: HTMLVideoElement | null = null;
let isEnabled = false;

// Create a tiny video element that plays in loop to prevent screen sleep (fallback)
const createNoSleepVideo = (): HTMLVideoElement => {
    const video = document.createElement("video");
    video.setAttribute("playsinline", "");
    video.setAttribute("muted", "");
    video.setAttribute("loop", "");
    video.setAttribute("title", "No Sleep");
    video.style.position = "absolute";
    video.style.left = "-100px";
    video.style.top = "-100px";
    video.style.width = "1px";
    video.style.height = "1px";

    // Tiny base64 encoded mp4 video (essentially empty but valid)
    // This is the "NoSleep" trick - a looping video keeps the screen awake
    video.src = "data:video/mp4;base64,AAAAIGZ0eXBpc29tAAACAGlzb21pc28yYXZjMW1wNDEAAAAIZnJlZQAAAAhtZGF0AAAA1m1vb3YAAABsbXZoZAAAAAAAAAAAAAAAAAAAA+gAAAAAAAEAAAEAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAAAAAQAAAAAAAAAAAAAAAAAAQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAIAAABidWR0YQAAAFptZXRhAAAAAAAAACFoZGxyAAAAAAAAAABtZGlyYXBwbAAAAAAAAAAAAAAAAC1pbHN0AAAAJal0b28AAAAdZGF0YQAAAAEAAAAATGF2ZjU4Ljc2LjEwMA==";

    return video;
};

// Try native Wake Lock API first
const requestNativeWakeLock = async (): Promise<boolean> => {
    if (!("wakeLock" in navigator)) {
        return false;
    }

    try {
        wakeLock = await navigator.wakeLock.request("screen");
        console.log("Native Wake Lock acquired");

        wakeLock.addEventListener("release", () => {
            console.log("Native Wake Lock released");
            wakeLock = null;
        });

        return true;
    } catch (err) {
        console.warn("Native Wake Lock failed:", err);
        return false;
    }
};

// Fallback: Use video trick for devices where native API doesn't work
const enableNoSleepVideo = async (): Promise<boolean> => {
    try {
        if (!noSleepVideo) {
            noSleepVideo = createNoSleepVideo();
            document.body.appendChild(noSleepVideo);
        }

        // Play needs to happen from user gesture
        await noSleepVideo.play();
        console.log("NoSleep video fallback enabled");
        return true;
    } catch (err) {
        console.warn("NoSleep video fallback failed:", err);
        return false;
    }
};

const disableNoSleepVideo = (): void => {
    if (noSleepVideo) {
        noSleepVideo.pause();
        noSleepVideo.src = "";
        if (noSleepVideo.parentNode) {
            noSleepVideo.parentNode.removeChild(noSleepVideo);
        }
        noSleepVideo = null;
        console.log("NoSleep video fallback disabled");
    }
};

export const requestWakeLock = async (): Promise<boolean> => {
    // Try native Wake Lock API first
    const nativeSuccess = await requestNativeWakeLock();

    if (nativeSuccess) {
        isEnabled = true;
        return true;
    }

    // Fall back to video trick
    const videoSuccess = await enableNoSleepVideo();
    if (videoSuccess) {
        isEnabled = true;
        return true;
    }

    console.error("All wake lock methods failed");
    return false;
};

export const releaseWakeLock = async (): Promise<void> => {
    isEnabled = false;

    // Release native wake lock
    if (wakeLock) {
        try {
            await wakeLock.release();
        } catch (e) {
            // Ignore errors
        }
        wakeLock = null;
    }

    // Disable video fallback
    disableNoSleepVideo();

    console.log("Wake Lock released");
};

export const isWakeLockActive = (): boolean => {
    const nativeActive = wakeLock !== null && !wakeLock.released;
    const videoActive = noSleepVideo !== null && !noSleepVideo.paused;
    return isEnabled && (nativeActive || videoActive);
};

export const isWakeLockSupported = (): boolean => {
    // We always support it now with the video fallback
    return true;
};

// Re-acquire wake lock when page becomes visible (after tab switch, etc.)
if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", async () => {
        if (isEnabled && document.visibilityState === "visible") {
            // Small delay to ensure the page is fully visible
            setTimeout(async () => {
                if (isEnabled) {
                    await requestWakeLock();
                }
            }, 100);
        }
    });
}
